'use client';

import type { ReactNode } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { HelpCircle } from 'lucide-react';
import { ThemeToggle } from '@paeffgen/react/crm-documents/ui';

/**
 * Schlanker Layout-Wrapper für ausgeloggte Auth-Seiten (/login, /register).
 * Zeigt statt der AppShell-Sidebar eine zentrierte Marken-Karte:
 * Logo → „Päffgen Kundenportal" → Kurzbeschreibung → Seiteninhalt.
 * Auth-Schutz übernimmt bereits die middleware.ts — hier nur die visuelle Verzweigung.
 */
export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-[100dvh] bg-surface flex flex-col items-center justify-center gap-6 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-8 shadow-sm">
        {/* Logo — Light/Dark-Variante analog AppShell */}
        <Image
          src="/logo-dachbaustoffe_color.svg"
          alt="Paeffgen Dachbaustoffe"
          width={300}
          height={76}
          className="mx-auto h-auto w-64 object-contain dark:hidden"
          priority
        />
        <Image
          src="/logo-dachbaustoffe_dark.svg"
          alt="Paeffgen Dachbaustoffe"
          width={300}
          height={76}
          className="mx-auto hidden h-auto w-64 object-contain dark:block"
          priority
        />

        {/* Marken-Titel + Kurzbeschreibung */}
        <h1 className="mt-6 text-center text-2xl font-semibold">Päffgen Kundenportal</h1>
        <p className="mt-3 text-center text-sm leading-relaxed text-text-muted">
          Ihre Belege, Artikel und Materialanfragen – jederzeit griffbereit in einem Portal.
        </p>

        {/* Seiteninhalt (Login bzw. Registrierung) */}
        <div className="mt-8">{children}</div>
      </div>

      {/* Hilfe/FAQ — öffentlich erreichbar, hilft besonders vor dem Login (Zugang, Kundennummer, Anmeldung) */}
      <Link
        href="/informationen"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-text-muted transition-colors hover:text-secondary"
      >
        <HelpCircle className="h-4 w-4" /> Hilfe &amp; FAQ
      </Link>

      {/* Theme-Umschalter (System / Hell / Dunkel) — auf Auth-Seiten sonst nicht erreichbar */}
      <ThemeToggle />
    </div>
  );
}
